import React, { useRef, useState } from "react";
import { ArrowRight, BarChart3, Compass, FilePlus2, HelpCircle, MessageCircleQuestion, Send, Sparkles, X } from "lucide-react";
import { trackEvent } from "../../utils/analytics";
import { apiUrl } from "../../utils/api";

type LandingGuideTile = {
  id: "chart" | "readings" | "observatory" | "tools" | "pricing" | "snapshots" | "other";
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>
  letter?: string;
};

const TILES: LandingGuideTile[] = [
  { id: "chart", letter: "A", label: "EXECUTE / FREE CHART", description: "Anchor your first visit with date, time, and place", icon: Compass },
  { id: "readings", letter: "B", label: "SCAN / FREE READINGS", description: "Choose a useful lens before you know what to ask", icon: Sparkles },
  { id: "observatory", letter: "C", label: "ENTER / OBSERVATORY", description: "Move from a flat reading into a living sky", icon: BarChart3 },
  { id: "tools", letter: "D", label: "FORGE / CUSTOM TOOL", description: "Turn a natural-language idea into a reusable method", icon: FilePlus2 },
  { id: "pricing", letter: "E", label: "UPGRADE / GUIDE ACCESS", description: "See how the concierge grows with your membership", icon: ArrowRight },
  { id: "snapshots", letter: "F", label: "REPLAY / SNAPSHOTS", description: "Compare the sky with events that mattered", icon: BarChart3 },
  { id: "other", label: "DEFINE / OTHER", description: "> define an outcome or command", icon: MessageCircleQuestion },
];

const routeFor = (id: LandingGuideTile["id"]): string | null => ({
  chart: "#chart",
  readings: "/readings",
  observatory: "/observatory",
  tools: "#commons",
  pricing: "#pricing",
  snapshots: "#snapshots",
  other: null,
}[id]);

export const LandingGuidePanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isNaming, setIsNaming] = useState(false);
  const [guideName, setGuideName] = useState(() => typeof window === "undefined" ? "NEX Guide" : localStorage.getItem("nex_guide_name") || "NEX Guide");
  const [draftName, setDraftName] = useState(guideName);
  const [question, setQuestion] = useState("");
  const [otherMode, setOtherMode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = (id: LandingGuideTile["id"]) => {
    const route = routeFor(id);
    if (!route) {
      setOtherMode(true);
      window.setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }
    if (route.startsWith("#")) document.getElementById(route.slice(1))?.scrollIntoView({ behavior: "smooth" });
    else window.location.assign(route);
    trackEvent("landing_guide_action", { action: id });
  };

  const submitQuestion = async (event: React.FormEvent) => {
    event.preventDefault();
    const request = question.trim();
    if (!request) return;
    const fallback = (): LandingGuideTile["id"] => {
      const normalized = request.toLowerCase();
      return normalized.includes("chart") || normalized.includes("birth") ? "chart" : normalized.includes("read") || normalized.includes("theme") ? "readings" : normalized.includes("tool") || normalized.includes("workflow") ? "tools" : normalized.includes("observatory") ? "observatory" : normalized.includes("price") || normalized.includes("tier") || normalized.includes("member") ? "pricing" : "other";
    };
    let match: LandingGuideTile["id"] = fallback();
    try {
      const response = await fetch(apiUrl("/api/guide/plan"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ request, surface: "landing", route: window.location.pathname }),
      });
      if (response.ok) {
        const payload = await response.json() as { selected_action?: { action_id?: string } };
        const plannedAction = payload.selected_action?.action_id;
        const plannedToTile: Record<string, LandingGuideTile["id"]> = {
          open_chart: "chart",
          open_readings: "readings",
          open_observatory: "observatory",
          open_forge: "tools",
          open_pricing: "pricing",
          open_snapshots: "snapshots",
          define_outcome: "other",
        };
        match = plannedAction ? plannedToTile[plannedAction] ?? match : match;
      }
    } catch {
      trackEvent("landing_guide_plan_fallback", { reason: "planner_unavailable" });
    }
    if (match === "other") {
      setOtherMode(true);
      setQuestion("");
      return;
    }
    navigate(match);
    setQuestion("");
  };

  const saveName = () => {
    const nextName = draftName.trim() || "NEX Guide";
    setGuideName(nextName);
    localStorage.setItem("nex_guide_name", nextName);
    setIsNaming(false);
    trackEvent("landing_guide_named", { name: nextName });
  };

  return (
    <>
      <button type="button" className="landing-guide-trigger" aria-label={`Open ${guideName}`} onClick={() => { setIsOpen((current) => !current); trackEvent("landing_guide_opened"); }}>
        <span className="landing-guide-trigger__signal" aria-hidden="true" />
        <Sparkles aria-hidden="true" />
        <span>{guideName}</span>
      </button>
      {isOpen && (
        <aside className="landing-guide-panel" aria-label={guideName}>
          <header>
            <div>
              <span className="landing-guide-kicker"><Sparkles /> NEX / access layer</span>
              <h2>{guideName}</h2>
              <p>Convert intent into the next useful move</p>
            </div>
            <button type="button" aria-label="Close guide" onClick={() => setIsOpen(false)}><X /></button>
          </header>
          <div className="landing-guide-meta"><span>CONTEXT / LANDING PAGE</span><span>MODE / READY</span><span>06 MOVES</span></div>
          <div className="landing-guide-name-row">
            {isNaming ? <><input value={draftName} onChange={(event) => setDraftName(event.target.value)} maxLength={32} autoFocus /><button type="button" onClick={saveName}>Save</button></> : <><span>Personalize the operator</span><button type="button" onClick={() => { setDraftName(guideName); setIsNaming(true); }}>Set guide name</button></>}
          </div>
          <div className="landing-guide-body">
            {otherMode && <div className="landing-guide-other"><HelpCircle /><p>Define an outcome in your own words</p></div>}
            {!otherMode && <div className="landing-guide-welcome"><HelpCircle /><p>Select a move or define an outcome</p></div>}
            <div className="landing-guide-tiles">{TILES.map((tile) => <button key={tile.id} type="button" onClick={() => tile.id === "other" ? setOtherMode(true) : navigate(tile.id)}><span className="landing-guide-option-key">{tile.letter ?? "+"}</span><tile.icon /><span><strong>{tile.label}</strong><small>{tile.description}</small></span><ArrowRight /></button>)}</div>
            <div className="landing-guide-progression"><span>Capability ladder</span><strong>Explore <i /> Observe <i /> Operate <i /> Lead</strong><small>Your guide gains reach as your membership grows</small></div>
          </div>
          <form onSubmit={submitQuestion}><input ref={inputRef} value={question} onChange={(event) => setQuestion(event.target.value)} placeholder={otherMode ? "> define an outcome or command" : "> define an outcome or command"} maxLength={500} /><button type="submit" aria-label="Execute command"><Send /></button></form>
        </aside>
      )}
    </>
  );
};
